/**
 * SceneManager — cena, camera, troca de foco e transicoes.
 *
 * D1 (inviolavel): camera FIXA, vista isometrica. Sem rotacao livre, sem
 * nenhum controle de camera pela crianca. O unico movimento permitido e o zoom
 * suave de transicao de foco, disparado pelo codigo — nunca pelo dedo dela.
 *
 * Nao existe OrbitControls neste projeto. Nem "so para testar" (secao 14).
 */

import * as THREE from 'three';

// Angulo isometrico fixo. A camera sempre olha do mesmo lugar, so muda o alvo.
const DIRECAO_ISO = new THREE.Vector3(1, 0.9, 1).normalize();
const DISTANCIA = 40;

// Enquadramento: quanto do mundo cabe na tela.
const ALTURA_FAZENDA = 34; // visao geral: a ilha inteira, com folga
const ALTURA_ANCORA = 11; // foco numa ancora

const DURACAO_TRANSICAO = 0.9; // segundos

export class SceneManager {
  constructor(canvas) {
    this.renderizador = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderizador.setSize(window.innerWidth, window.innerHeight);
    this.renderizador.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderizador.shadowMap.enabled = false; // orcamento de performance

    this.cena = new THREE.Scene();
    // O ceu comeca definido aqui, e nao so no CicloDia: se por algum motivo o
    // fundo da cena nao pegar, o renderizador ainda limpa a tela com a cor
    // certa em vez do cinza padrao.
    this.cena.background = new THREE.Color(0x8ed0e8);
    this.renderizador.setClearColor(0x8ed0e8, 1);

    this.alvo = new THREE.Vector3(0, 2, 0);
    this.altura = ALTURA_FAZENDA;

    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 200);
    this.aplicarEnquadramento();

    // Estado da transicao suave.
    this.transicao = null;

    window.addEventListener('resize', () => this.aoRedimensionar());
  }

  aoRedimensionar() {
    this.renderizador.setSize(window.innerWidth, window.innerHeight);
    this.aplicarEnquadramento();
  }

  aplicarEnquadramento() {
    const proporcao = window.innerWidth / window.innerHeight;
    const metadeAltura = this.altura / 2;
    const metadeLargura = metadeAltura * proporcao;

    this.camera.left = -metadeLargura;
    this.camera.right = metadeLargura;
    this.camera.top = metadeAltura;
    this.camera.bottom = -metadeAltura;
    this.camera.updateProjectionMatrix();

    this.camera.position.copy(this.alvo).addScaledVector(DIRECAO_ISO, DISTANCIA);
    this.camera.lookAt(this.alvo);
  }

  /** Zoom suave ate uma ancora. Chamado pelo codigo, nunca pela crianca. */
  focarEm(posicao) {
    this.iniciarTransicao(posicao.clone(), ALTURA_ANCORA);
  }

  /** Volta para a visao geral da fazenda. */
  voltarParaFazenda() {
    // Mira na superficie do terreno, nao no fundo dos blocos.
    this.iniciarTransicao(new THREE.Vector3(0, 2, 0), ALTURA_FAZENDA);
  }

  iniciarTransicao(alvoFinal, alturaFinal) {
    this.transicao = {
      alvoInicial: this.alvo.clone(),
      alvoFinal,
      alturaInicial: this.altura,
      alturaFinal,
      t: 0,
    };
  }

  atualizar(dt) {
    if (!this.transicao) return;

    const tr = this.transicao;
    tr.t = Math.min(1, tr.t + dt / DURACAO_TRANSICAO);
    const suave = suavizar(tr.t);

    this.alvo.lerpVectors(tr.alvoInicial, tr.alvoFinal, suave);
    this.altura = tr.alturaInicial + (tr.alturaFinal - tr.alturaInicial) * suave;
    this.aplicarEnquadramento();

    if (tr.t >= 1) this.transicao = null;
  }

  renderizar() {
    this.renderizador.render(this.cena, this.camera);
  }

  /**
   * Quantos pixels vale uma unidade de mundo QUANDO a camera terminar de focar.
   * Serve para conferir D3 sem depender do meio da transicao: o que importa e
   * o tamanho que o alvo tera quando a crianca for tocar nele.
   */
  pixelsPorUnidadeNoFoco() {
    const alturaFinal = this.transicao?.alturaFinal ?? this.altura;
    return this.renderizador.domElement.clientHeight / alturaFinal;
  }

  adicionar(objeto) {
    this.cena.add(objeto);
  }

  remover(objeto) {
    this.cena.remove(objeto);
  }
}

/** Aceleracao e desaceleracao suaves — nada de movimento brusco na tela. */
function suavizar(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}
